import React from 'react';
import { argsToStringifiable } from '@local/finsera-core/src/helpers/embed/utils';
import { _, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import Chart from './chart';
import { useUiStyles, useWindowDimensions } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';

type RadarChartProps = {
  id: string;
  data: types.widgets.TableData;
  labels: types.widgets.TableData;
  dimensions: string[];
  fullScreen: boolean;
  readOnly: boolean;

  alert?: {
    type: 'error' | 'warning';
    message: string;
  };
  exportTitle?: string;
  exportSubTitle?: string;
  setExternalEmbed?: (_json: types.analysis.EmbedChartData) => void;
  download?: boolean;
};

/**
 * Radar chart component that displays data in a radar/spider chart format
 *
 * @param id - Unique identifier for the chart
 * @param data - Data to be displayed in the chart
 * @param labels - Labels data for the chart
 * @param dimensions - Array of dimensions to be displayed in the radar chart
 * @param fullScreen - Whether the chart is in fullscreen mode
 * @param readOnly - Whether the chart is in read-only mode
 * @param alert - Alert message to display above the chart
 * @param exportTitle - Title used when exporting the chart
 * @param exportSubTitle - Subtitle used when exporting the chart
 * @param setExternalEmbed - Callback to set external embed data
 * @param download - Whether to show download button
 * @returns React component displaying a radar chart
 */
const RadarChart = (args: RadarChartProps) => {
  const {
    id,
    data,
    labels,
    dimensions,
    fullScreen,
    readOnly,

    alert,
    exportTitle,
    exportSubTitle,
    setExternalEmbed,
    download = true,
  } = args;

  const uiStyles = useUiStyles();
  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);

  const { width } = useWindowDimensions(1_000);

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (setExternalEmbed)
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'radar-chart-v5',
          args: argsToStringifiable({
            ...args,
            data,
          }),
        });

      setLocalData(data);
    };

    handleData();
  }, [data, width]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: `${alert ? 'auto ' : ''} 1fr`,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}

      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box sx={{ height: fullScreen ? 'calc(100% - 40px)' : '100%' }}>
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <Chart
              id={id}
              data={data}
              labels={labels}
              dimensions={dimensions}
              fullScreen={fullScreen}
              download={download}
            />
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default RadarChart;
